import type { ReactNode } from 'react';
import './PageHeader.css';

export interface PageHeaderProps {
  title: ReactNode;
  /** The lead paragraph. Capped at a reading measure, not the content width. */
  description?: ReactNode;
  /**
   * Breadcrumbs, rendered above the title. Takes a `Breadcrumbs` element — kept
   * as a slot rather than a prop list so a screen can pass the app's own router
   * links without this component knowing about routing.
   */
  breadcrumbs?: ReactNode;
  /** Buttons, right-aligned on wide viewports and below the title on narrow ones. */
  actions?: ReactNode;
  /**
   * The entity variant: a 44px identity tile beside the title. `tileTone` tints
   * it. Used by Source Detail and Finding Detail in the prototype.
   */
  tile?: ReactNode;
  tileTone?: 'accent' | 'success' | 'warning' | 'danger' | 'neutral';
  /** A state badge rendered inline with the title, not beneath it. */
  badge?: ReactNode;
  /** A mono meta line (an instance string, a record id) under the title. */
  meta?: ReactNode;
  /**
   * A row of short facts under the title — "scope · first seen · recalculated".
   * Distinct from `meta`: facts wrap independently, a meta line does not.
   */
  facts?: ReactNode;
  /**
   * The impact variant: a large right-aligned figure with a caption. Moves under
   * the title on narrow viewports, where a two-column header cannot hold.
   */
  figure?: ReactNode;
  figureCaption?: ReactNode;
  /**
   * `h1` by default. A screen has exactly one page header, so `h1` is almost
   * always right — but hardcoding the level is the same heading-outline defect
   * `Card` documents, and `3` is genuinely needed: the component gallery renders
   * two headers *inside* an `h2`-titled section, and the first version of that
   * put three `<h1>`s on one page. The visual suite caught it by failing on an
   * ambiguous heading locator, which is a better outcome than the page shipping
   * with three top-level headings and axe staying quiet about sibling levels.
   */
  headingLevel?: 1 | 2 | 3;
  /** `1080px` default; `narrow` (900) for forms, `review` (1160) for the pair view. */
  measure?: 'default' | 'wide' | 'narrow' | 'review';
  /**
   * Whether this component supplies the page frame — the centred measure and the `clamp()`
   * gutter — or inherits one from an ancestor.
   *
   * ⚠️ **`frame="none"` is why this component had no product consumer, and the reason is
   * structural rather than neglect ([B-94](../../../docs/platform/TODO.md)).** `AppShell` already
   * applies both: `.dpr-shell__content` carries the fluid gutter and `.dpr-shell__measure` carries
   * the centred max-width. Rendering the default frame inside it therefore **doubles the gutter**
   * and re-applies a measure inside a narrower one — so every screen in `apps/web` hand-rolled an
   * `<h1>` instead, four of them, at three sizes. Neither half of that is discoverable from either
   * component's docs, which is the actual defect: a catalogue entry that cannot be composed with
   * the shell every screen lives in is bypassed for a good reason, and the fix is to make the
   * composition expressible.
   *
   * `page` (default) keeps the standalone behaviour — the guest remediation workspace and the
   * auth screens render outside any shell and need it.
   */
  frame?: 'page' | 'none';
}

/**
 * The page header and the page frame (design-system catalogue, Tier 2).
 *
 * **17 of the prototype's 18 page files.** With `Callout` and `Breadcrumbs`, one
 * of three small components that unblock every screen — which is why they are
 * built before anything larger (prototype/CENSUS.md §2a).
 *
 * It owns the **frame** as well as the header, and that is the important part:
 * the prototype centres every screen with `margin: 0 auto` inside a fluid
 * `clamp()` gutter, and the build never centred anything, so content ran
 * flush-left against a 12px gutter on a wide monitor (audit gap F-6). Leaving the
 * frame to each screen is how that recurs.
 *
 * Four variants come off one part set rather than four components, because the
 * prototype composes them freely — Finding Detail is entity *and* impact:
 *
 *   plain     title + lead
 *   actions   + a right-aligned button cluster
 *   entity    + a 44px identity tile, an inline badge, and a meta or fact row
 *   impact    + a large right-aligned figure and caption
 */
export function PageHeader({
  title,
  description,
  breadcrumbs,
  actions,
  tile,
  tileTone = 'neutral',
  badge,
  meta,
  facts,
  figure,
  figureCaption,
  headingLevel = 1,
  measure = 'default',
  frame = 'page',
}: PageHeaderProps) {
  const Heading = `h${headingLevel}` as 'h1' | 'h2' | 'h3';
  return (
    <header
      className={
        frame === 'none'
          ? 'dpr-page-header dpr-page-header--unframed'
          : `dpr-page-header dpr-page-header--${measure}`
      }
    >
      {breadcrumbs}
      <div className="dpr-page-header__row">
        <div className="dpr-page-header__main">
          <div className="dpr-page-header__identity">
            {tile ? (
              <span
                className={`dpr-page-header__tile dpr-page-header__tile--${tileTone}`}
                aria-hidden="true"
              >
                {tile}
              </span>
            ) : null}
            <div className="dpr-page-header__titles">
              <Heading className="dpr-page-header__title">
                {title}
                {badge ? <span className="dpr-page-header__badge">{badge}</span> : null}
              </Heading>
              {meta ? <p className="dpr-page-header__meta">{meta}</p> : null}
              {facts ? <div className="dpr-page-header__facts">{facts}</div> : null}
            </div>
          </div>
          {description ? <p className="dpr-page-header__description">{description}</p> : null}
        </div>
        {figure ? (
          <div className="dpr-page-header__figure">
            <span className="dpr-page-header__figure-value">{figure}</span>
            {figureCaption ? (
              <span className="dpr-page-header__figure-caption">{figureCaption}</span>
            ) : null}
          </div>
        ) : null}
        {actions ? <div className="dpr-page-header__actions">{actions}</div> : null}
      </div>
    </header>
  );
}

export interface PageProps {
  children: ReactNode;
  measure?: 'default' | 'wide' | 'narrow' | 'review';
}

/**
 * The centred page frame, for screens that want the measure and gutter without a
 * header (or that render their own). `PageHeader` applies the same frame itself,
 * so a screen uses one or the other — nesting them would double the gutter.
 */
export function Page({ children, measure = 'default' }: PageProps) {
  return <div className={`dpr-page dpr-page--${measure}`}>{children}</div>;
}
